import React, { useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";
import InformationLayerMap from "../components/InformationLayerMap";
import api from "../services/httpClient";
import PaginationComponent from "../components/PaginationComponent";
import LayersTable from "../components/Layers/LayersTable";
import LayersIcon from "../assets/images/server_rgb_red.svg";
import "../assets/styles/layers.scss";
import LayersButtons from "../components/Layers/LayersButtons";

const Layers = () => {
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [filters, setFilters] = useState({});
  const filtersRef = useRef({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: "Id", direction: "asc" });
  const [rememberedTotalCount, setRememberedTotalCount] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchVersion, setFetchVersion] = useState(0);
  const [selectedLayer, setSelectedLayer] = useState(null);

  const currentPageRef = useRef(currentPage);
  const itemsPerPageRef = useRef(itemsPerPage);
  const appliedFiltersRef = useRef(appliedFilters);
  const sortConfigRef = useRef(sortConfig);
  const lastFetchedVersionRef = useRef(0);
  const abortControllerRef = useRef(null);

  filtersRef.current = filters;
  currentPageRef.current = currentPage;
  itemsPerPageRef.current = itemsPerPage;
  appliedFiltersRef.current = appliedFilters;
  sortConfigRef.current = sortConfig;

  const [data, setData] = useState({
    items: [],
    itemsCount: null,
    totalRecords: 0,
    totalPages: 0,
    pageNumber: 1,
    pageSize: itemsPerPage,
  });

  React.useEffect(() => {
    if (lastFetchedVersionRef.current === fetchVersion) return;
    lastFetchedVersionRef.current = fetchVersion;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    let cancelled = false;
    const pageNumber = currentPageRef.current;
    const pageSize = itemsPerPageRef.current;
    const filtersToApply = appliedFiltersRef.current;
    const sort = sortConfigRef.current;

    (async () => {
      setIsLoading(true);
      try {
        const effectiveSortConfig =
          sort && sort.key ? sort : { key: "Id", direction: "asc" };

        const response = await api.get(`/Layer/List`, {
          params: {
            pageNumber,
            pageSize,
            SortBy: effectiveSortConfig.key,
            SortDirection: effectiveSortConfig.direction,
            ...filtersToApply,
          },
          signal: controller.signal,
        });

        if (cancelled) return;

        const responsePageNumber = Number(response.data.pageNumber || pageNumber);
        const responseTotalCountRaw =
          response.data.totalCount ??
          response.data.itemsCount ??
          response.data.totalRecords;
        const responseTotalCount =
          Number.isFinite(Number(responseTotalCountRaw)) &&
          Number(responseTotalCountRaw) >= 0
            ? Number(responseTotalCountRaw)
            : null;

        if (responsePageNumber === 1) {
          setRememberedTotalCount(responseTotalCount);
        }

        setData({
          items: Array.isArray(response.data.items) ? response.data.items : [],
          itemsCount: responseTotalCount,
          totalRecords: response.data.totalRecords || 0,
          totalPages: response.data.totalPages || 0,
          pageNumber: response.data.pageNumber || 1,
          pageSize: response.data.pageSize || pageSize,
        });
      } catch (error) {
        if (cancelled || error?.name === "CanceledError" || error?.name === "AbortError") return;

        console.error("Error fetching layers:", error);
        setData({
          items: [],
          itemsCount: null,
          totalRecords: 0,
          totalPages: 0,
          pageNumber: 1,
          pageSize,
        });
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [fetchVersion]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const incomingFilters = {};

    params.forEach((value, key) => {
      incomingFilters[key] = value;
    });

    if (Object.keys(incomingFilters).length > 0) {
      setFilters(incomingFilters);
      filtersRef.current = incomingFilters;
      setAppliedFilters(incomingFilters);
      setCurrentPage(1);
      setRememberedTotalCount(null);
    }

    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  }, [location.search]);

  const handleSearch = (searchTerm) => {
    const newFilters = searchTerm ? { Name: searchTerm } : {};
    setFilters(newFilters);
    filtersRef.current = newFilters;
    setAppliedFilters(newFilters);
    setCurrentPage(1);
    setRememberedTotalCount(null);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  const handleSelectionChange = (selectedIds) => {
    console.log("Zaznaczone warstwy:", selectedIds);
  };

  const handleSort = (newSortConfig) => {
    setSortConfig(newSortConfig);
    setCurrentPage(1);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  const handlePageChange = (page) => {
    if (Number(page) === Number(currentPageRef.current)) return;
    setCurrentPage(page);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  const handleItemsPerPageChange = (pageSize) => {
    setItemsPerPage(pageSize);
    setCurrentPage(1);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  const handleLayerClick = (layer) => {
    setSelectedLayer(layer);
  };

  return (
    <div className="layers-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col>
            <div className="d-flex align-items-center">
              <img
                src={LayersIcon}
                alt="Layers Icon"
                style={{ width: "30px", height: "30px", marginRight: "12px" }}
              />
              <h3 style={{ fontSize: "1.5rem", fontWeight: "bold", margin: 0 }}>
                Lista zdefiniowanych warstw
              </h3>
            </div>
          </Col>
        </Row>
        <Row className="align-items-center mb-4">
          <LayersButtons onSearch={handleSearch} />
        </Row>
        <Row>
          <Col xs={12} md={4}>
            <Row>
              <LayersTable
                layersData={data.items}
                isLoading={isLoading}
                onSelectionChange={handleSelectionChange}
                onSort={handleSort}
                onLayerClick={handleLayerClick}
              />
            </Row>
            <Row className="align-items-center mt-4">
              <PaginationComponent
                itemsPerPage={data.pageSize}
                dataCount={data.items.length}
                itemsCount={data.itemsCount ?? rememberedTotalCount}
                totalCount={rememberedTotalCount}
                pageNumber={data.pageNumber}
                totalPages={data.totalPages}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                itemsPerPageOptions={[20, 100, 500]}
              />
            </Row>
          </Col>
          <Col xs={12} md={8}>
            <InformationLayerMap
              area={selectedLayer?.area}
              color={selectedLayer?.color || "#FF0000"}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Layers;
